// @ts-check
/* eslint-disable implicit-arrow-linebreak, import/no-unresolved, indent, sort-imports */
import {Animated, View} from "react-native"
import React, {useMemo} from "react"
import classNames from "classnames"
import Column from "./components/column"
import ColumnContent from "./column-content"
import columnIdentifier from "./column-identifier.js"
import {EventEmitter} from "eventemitter3"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import memo from "set-state-compare/build/memo.js"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import Text from "../utils/text"
import {useBreakpoint} from "responsive-breakpoints"
import useI18n from "i18n-on-steroids/build/src/use-i18n.js"

/** @typedef {import("../base-model.js").default} BaseModel */
/** @typedef {{attribute?: string, columnProps?: {className?: string, style?: object}, commonProps?: {className?: string}}} TableColumnDefinition */
/** @typedef {{identifier(): string}} TableSettingColumn */
/**
 * @typedef {object} TableLike
 * @property {(column: TableColumnDefinition) => {style?: object}} columnProps
 * @property {(column: TableColumnDefinition) => string} headerLabelForColumn
 * @property {(args: object) => object} styleForColumn
 */

/**
 * @typedef {object} Props
 * @property {Animated.ValueXY} animatedPosition
 * @property {Animated.Value} animatedWidth
 * @property {Animated.Value} animatedZIndex
 * @property {TableColumnDefinition} column
 * @property {number} columnIndex
 * @property {boolean} even
 * @property {EventEmitter} events
 * @property {BaseModel} model
 * @property {TableLike} table
 * @property {TableSettingColumn} tableSettingColumn
 */
/** @typedef {Record<string, never>} State */
export default memo(shapeComponent(/** @augments {ShapeComponent<Props, State>} */ class ApiMakerTableModelColumn extends ShapeComponent {
  static propTypes = propTypesExact({
    animatedPosition: PropTypes.instanceOf(Animated.ValueXY).isRequired,
    animatedWidth: PropTypes.instanceOf(Animated.Value).isRequired,
    animatedZIndex: PropTypes.instanceOf(Animated.Value).isRequired,
    column: PropTypes.object.isRequired,
    columnIndex: PropTypes.number.isRequired,
    even: PropTypes.bool.isRequired,
    events: PropTypes.instanceOf(EventEmitter).isRequired,
    model: PropTypes.object.isRequired,
    table: PropTypes.object.isRequired,
    tableSettingColumn: PropTypes.object.isRequired
  })

  render() {
    const {l, t} = useI18n({namespace: "js.api_maker.table.model_column"})
    const {mdUp} = useBreakpoint()
    const {animatedWidth, animatedZIndex, column, columnIndex, even, model, table} = this.props
    const columnProps = table.columnProps(column)
    const {style, ...restColumnProps} = columnProps
    const actualStyle = useMemo(() =>
      Object.assign(
        table.styleForColumn({
          column,
          columnIndex,
          even,
          style: {
            zIndex: animatedZIndex,
            transform: this.p.animatedPosition.getTranslateTransform(),
            width: mdUp ? animatedWidth : "100%"
          }
        }),
        style
      ),
      [column, columnIndex, even, animatedZIndex, mdUp, animatedWidth, style]
    )

    const className = classNames(this.columnClassNamesForColumn(column))
    const identifier = columnIdentifier(column)
    const dataSet = useMemo(() => ({class: className, identifier}), [className])

    return (
      <Column
        dataSet={dataSet}
        style={actualStyle}
        {...restColumnProps}
      >
        {!mdUp &&
          <View dataSet={this.cache("columnLabelDataSet", {class: "table--column-label"})}>
            <Text style={this.cache("columnLabelTextStyle", {fontWeight: "bold"})}>
              {table.headerLabelForColumn(column)}
            </Text>
          </View>
        }
        <View dataSet={this.cache("columnValueDataSet", {class: "table--column-value"})}>
          {new ColumnContent({column, l, model, t, table}).content()}
        </View>
      </Column>
    )
  }

  columnClassNamesForColumn(column) {
    const classNames = ["table--column"]

    if (column.commonProps && column.commonProps.className) classNames.push(column.commonProps.className)
    if (column.columnProps && column.columnProps.className) classNames.push(column.columnProps.className)

    return classNames
  }
}))
